import * as THREE from 'three';
import { LOW, cachedTexture } from '../core/tier.js';
import { markDynamic } from '../world/freezeStatics.js';

const UP = new THREE.Vector3(0, 1, 0);
const _mid = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _jitter = new THREE.Vector3();

const BOLT_POOL = LOW ? 2 : 4;
const SEGMENTS = LOW ? 5 : 7;
const SPARKS = LOW ? 8 : 14;
const LIFE = 0.34;

function makeGlowTexture() {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const ctx = c.getContext('2d');
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.35, 'rgba(185,230,255,0.78)');
  g.addColorStop(1, 'rgba(125,180,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

const glowTex = cachedTexture('wizard-lightning-glow', makeGlowTexture);

function hideBolt(bolt) {
  bolt.life = 0;
  bolt.root.visible = false;
  bolt.flash.visible = false;
  bolt.hitGlow.visible = false;
  bolt.sparkMat.opacity = 0;
  for (const seg of bolt.segments) seg.visible = false;
}

function placeSegment(seg, from, to, radius, opacity) {
  _dir.subVectors(to, from);
  const len = _dir.length();
  if (len < 0.01) {
    seg.visible = false;
    return;
  }
  _mid.addVectors(from, to).multiplyScalar(0.5);
  seg.visible = true;
  seg.position.copy(_mid);
  seg.quaternion.setFromUnitVectors(UP, _dir.multiplyScalar(1 / len));
  seg.scale.set(radius, len, radius);
  seg.material.opacity = opacity;
}

function randomJitter(out, spread) {
  out.set(
    (Math.random() - 0.5) * spread,
    (Math.random() - 0.5) * spread * 0.35,
    (Math.random() - 0.5) * spread
  );
}

export function createWizardLightningFx({ parent }) {
  const bolts = [];
  const segmentGeo = new THREE.CylinderGeometry(1, 1, 1, LOW ? 5 : 6, 1, true);

  for (let i = 0; i < BOLT_POOL; i++) {
    const root = new THREE.Group();
    markDynamic(root);   // strikes reposition/rescale the bolt segments
    root.visible = false;
    parent.add(root);

    const segments = [];
    for (let s = 0; s < SEGMENTS; s++) {
      const mat = new THREE.MeshBasicMaterial({
        color: s % 2 ? 0x9be7ff : 0xffffff,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        fog: false,
      });
      const mesh = new THREE.Mesh(segmentGeo, mat);
      mesh.visible = false;
      root.add(mesh);
      segments.push(mesh);
    }

    const flashMat = new THREE.SpriteMaterial({
      map: glowTex,
      color: 0xc9f4ff,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      fog: false,
    });
    const flash = new THREE.Sprite(flashMat);
    root.add(flash);

    const hitMat = new THREE.SpriteMaterial({
      map: glowTex,
      color: 0x8ff6ff,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      fog: true,
    });
    const hitGlow = new THREE.Sprite(hitMat);
    root.add(hitGlow);

    const sparkPositions = new Float32Array(SPARKS * 3);
    const sparkDirs = new Float32Array(SPARKS * 3);
    const sparkGeo = new THREE.BufferGeometry();
    const sparkAttr = new THREE.BufferAttribute(sparkPositions, 3);
    sparkAttr.setUsage(THREE.DynamicDrawUsage);
    sparkGeo.setAttribute('position', sparkAttr);
    const sparkMat = new THREE.PointsMaterial({
      map: glowTex,
      color: 0xbff7ff,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      fog: true,
      size: LOW ? 0.16 : 0.14,
      sizeAttenuation: true,
    });
    const sparks = new THREE.Points(sparkGeo, sparkMat);
    sparks.frustumCulled = false;
    root.add(sparks);

    bolts.push({
      root, segments, flash, flashMat, hitGlow, hitMat,
      sparks, sparkGeo, sparkPositions, sparkDirs, sparkMat,
      life: 0,
    });
  }

  function strike(from, to) {
    const bolt = bolts.find(b => b.life <= 0) || bolts[0];
    const pts = [];
    for (let i = 0; i <= SEGMENTS; i++) {
      const p = new THREE.Vector3().lerpVectors(from, to, i / SEGMENTS);
      if (i > 0 && i < SEGMENTS) {
        randomJitter(_jitter, 0.42);
        p.add(_jitter);
      }
      pts.push(p);
    }
    for (let i = 0; i < SEGMENTS; i++) {
      placeSegment(bolt.segments[i], pts[i], pts[i + 1], i % 2 ? 0.027 : 0.04, 1);
    }
    bolt.flash.position.copy(from);
    bolt.flash.scale.set(0.45, 0.45, 1);
    bolt.flashMat.opacity = 0.95;
    bolt.flash.visible = true;

    bolt.hitGlow.position.copy(to);
    bolt.hitGlow.scale.set(0.9, 0.9, 1);
    bolt.hitMat.opacity = 0.95;
    bolt.hitGlow.visible = true;

    for (let i = 0; i < SPARKS; i++) {
      const j = i * 3;
      const a = Math.random() * Math.PI * 2;
      const r = 0.5 + Math.random() * 0.85;
      bolt.sparkPositions[j] = to.x;
      bolt.sparkPositions[j + 1] = to.y;
      bolt.sparkPositions[j + 2] = to.z;
      bolt.sparkDirs[j] = Math.cos(a) * r;
      bolt.sparkDirs[j + 1] = 0.25 + Math.random() * 1.1;
      bolt.sparkDirs[j + 2] = Math.sin(a) * r;
    }
    bolt.sparkGeo.attributes.position.needsUpdate = true;
    bolt.sparkMat.opacity = 0.95;
    bolt.life = LIFE;
    bolt.root.visible = true;
  }

  function update(dt) {
    for (const bolt of bolts) {
      if (bolt.life <= 0) continue;
      bolt.life -= dt;
      if (bolt.life <= 0) {
        hideBolt(bolt);
        continue;
      }
      const k = 1 - bolt.life / LIFE;
      const fade = Math.max(0, 1 - k);
      for (let i = 0; i < bolt.segments.length; i++) {
        const seg = bolt.segments[i];
        seg.material.opacity = fade * (i % 2 ? 0.55 : 0.85);
        seg.scale.x *= 0.94;
        seg.scale.z *= 0.94;
      }
      bolt.flashMat.opacity = fade * 0.8;
      bolt.flash.scale.setScalar(0.45 + k * 0.45);
      bolt.hitMat.opacity = fade * 0.85;
      bolt.hitGlow.scale.setScalar(0.9 + k * 1.3);
      bolt.sparkMat.opacity = fade * 0.95;

      for (let i = 0; i < SPARKS; i++) {
        const j = i * 3;
        bolt.sparkPositions[j] += bolt.sparkDirs[j] * dt * 2.4;
        bolt.sparkPositions[j + 1] += (bolt.sparkDirs[j + 1] - k * 1.4) * dt * 2.4;
        bolt.sparkPositions[j + 2] += bolt.sparkDirs[j + 2] * dt * 2.4;
      }
      bolt.sparkGeo.attributes.position.needsUpdate = true;
    }
  }

  function clear() {
    for (const bolt of bolts) hideBolt(bolt);
  }

  return { strike, update, clear };
}
